import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ChipInput } from "@/components/ChipInput";
import { toast } from "sonner";
import {
  Plus,
  PencilSimple,
  Trash,
  UploadSimple,
  Columns,
  Users,
  ClockCounterClockwise,
  ListChecks,
  CheckCircle,
  XCircle,
  MapPin,
  DownloadSimple,
  ImageSquare,
  X as CloseIcon,
  CopySimple,
} from "@phosphor-icons/react";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const emptyForm = {
  service_name: "",
  category: "",
  price_per_unit: "",
  duration: "",
  moq: "",
  cities: [],
  images: [],
  extra_fields: {},
};

export default function Admin() {
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ categories: [], cities: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, u, l, m] = await Promise.all([
        api.get("/services"),
        api.get("/columns"),
        api.get("/users"),
        api.get("/login-logs"),
        api.get("/services/meta"),
      ]);
      setServices(s.data); setColumns(c.data); setUsers(u.data); setLogs(l.data); setMeta(m.data);
    } catch { toast.error("Failed to load admin data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-canvas" data-testid="admin-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 animate-fade-up">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3 font-bold">Admin Console</div>
            <h1 className="font-display font-black text-zinc-900 text-4xl sm:text-5xl leading-[0.95] tracking-tight">
              Publish. Price. <span className="text-brand italic">Ship.</span>
            </h1>
            <p className="text-zinc-500 mt-3 max-w-2xl">
              Manage services, table columns, sales users and review the login audit trail.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="bg-white border border-zinc-200 p-1 rounded-lg inline-flex mb-8 shadow-sm"
            data-testid="admin-tabs"
          >
            <TabsTrigger value="services" data-testid="tab-services" className="data-[state=active]:bg-brand data-[state=active]:text-white rounded-md px-4 py-2 gap-2 text-zinc-600">
              <ListChecks size={16} weight="bold" /> Services
            </TabsTrigger>
            <TabsTrigger value="columns" data-testid="tab-columns" className="data-[state=active]:bg-brand data-[state=active]:text-white rounded-md px-4 py-2 gap-2 text-zinc-600">
              <Columns size={16} weight="bold" /> Columns
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-brand data-[state=active]:text-white rounded-md px-4 py-2 gap-2 text-zinc-600">
              <Users size={16} weight="bold" /> Sales Users
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" className="data-[state=active]:bg-brand data-[state=active]:text-white rounded-md px-4 py-2 gap-2 text-zinc-600">
              <ClockCounterClockwise size={16} weight="bold" /> Login Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            <ServicesPanel services={services} columns={columns} citySuggestions={meta.cities} onChange={load} />
          </TabsContent>
          <TabsContent value="columns">
            <ColumnsPanel columns={columns} onChange={load} />
          </TabsContent>
          <TabsContent value="users">
            <UsersPanel users={users} onChange={load} />
          </TabsContent>
          <TabsContent value="logs">
            <LogsPanel logs={logs} />
          </TabsContent>
        </Tabs>

        {loading && <div className="text-zinc-400 text-sm mt-4">Loading...</div>}
      </main>
    </div>
  );
}

/* ================= Services Panel ================= */
function ServicesPanel({ services, columns, citySuggestions, onChange }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const customColumns = columns.filter((c) => !c.system);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, cities: [], extra_fields: Object.fromEntries(customColumns.map((c) => [c.key, ""])) });
    setSheetOpen(true);
  };
  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      service_name: svc.service_name || "",
      category: svc.category || "",
      price_per_unit: svc.price_per_unit ?? "",
      duration: svc.duration || "",
      moq: svc.moq || "",
      cities: Array.isArray(svc.cities) ? [...svc.cities] : (svc.city ? [svc.city] : []),
      images: Array.isArray(svc.images) ? [...svc.images] : [],
      extra_fields: { ...(svc.extra_fields || {}) },
    });
    setSheetOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, price_per_unit: Number(form.price_per_unit || 0) };
      if (editing) {
        await api.put(`/services/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/services", payload);
        toast.success("Service added");
      }
      setSheetOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.service_name}"?`)) return;
    try {
      await api.delete(`/services/${svc.id}`);
      toast.success("Deleted");
      onChange();
    } catch { toast.error("Delete failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("bw_token");
      const res = await fetch(`${API}/services/template.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brandonwheelz_template.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Template download failed");
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/services/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const cellValue = (svc, c) => {
    if (c.key === "price_per_unit") return INR.format(svc.price_per_unit || 0);
    if (c.key === "cities") {
      const list = Array.isArray(svc.cities) ? svc.cities : (svc.city ? [svc.city] : []);
      if (list.length === 0) return <span className="text-zinc-400">—</span>;
      return (
        <div className="flex flex-wrap gap-1.5">
          {list.map((v, i) => <span key={`${v}-${i}`} className="city-chip"><MapPin size={11} weight="fill" />{v}</span>)}
        </div>
      );
    }
    if (c.system) return svc[c.key] ?? "";
    return (svc.extra_fields || {})[c.key] ?? "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="text-zinc-600 text-sm">
          <span className="font-mono-num text-zinc-900 text-lg mr-1 font-bold">{services.length}</span> services in catalog
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="bg-white border-zinc-300 hover:bg-brand-soft hover:border-brand hover:text-brand text-zinc-700 gap-2"
          >
            <DownloadSimple size={16} weight="bold" /> Download Template
          </Button>
          <Button
            variant="outline"
            onClick={() => { setUploadResult(null); setFile(null); setUploadOpen(true); }}
            data-testid="bulk-upload-btn"
            className="bg-white border-zinc-300 hover:bg-brand-soft hover:border-brand hover:text-brand text-zinc-700 gap-2"
          >
            <UploadSimple size={16} weight="bold" /> Bulk CSV Upload
          </Button>
          <Button
            onClick={openAdd}
            data-testid="add-service-btn"
            className="bg-brand hover:bg-brand-hover text-white gap-2 shadow-sm"
          >
            <Plus size={16} weight="bold" /> Add Service
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-auto max-h-[calc(100vh-22rem)]">
          <table className="pricelist" data-testid="admin-services-table">
            <thead>
              <tr>
                {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => (
                <tr key={svc.id} data-testid={`admin-row-${svc.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.key === "price_per_unit" ? "font-mono-num text-brand font-bold" : c.key === "service_name" ? "text-zinc-900 font-semibold" : ""}>
                      {cellValue(svc, c)}
                    </td>
                  ))}
                  <td className="text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(svc)}
                      data-testid={`edit-${svc.id}`}
                      className="text-zinc-600 hover:text-brand hover:bg-brand-soft gap-1"
                    >
                      <PencilSimple size={14} /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(svc)}
                      data-testid={`delete-${svc.id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                    >
                      <Trash size={14} /> Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit / Add sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="bg-white border-l border-zinc-200 text-zinc-900 w-full sm:max-w-lg overflow-y-auto"
          data-testid="service-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-display text-zinc-900 text-2xl">
              {editing ? "Edit service" : "Add a new service"}
            </SheetTitle>
            <SheetDescription className="text-zinc-500">
              {editing ? "Update the details and save." : "Enter the service details. It'll appear instantly on the pricelist."}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-6">
            <Field label="Service Name" v={form.service_name} onChange={(v) => setForm({ ...form, service_name: v })} testid="f-name" />
            <Field label="Category" v={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="f-category" />
            <Field label="Price Per Unit (INR)" type="number" v={form.price_per_unit} onChange={(v) => setForm({ ...form, price_per_unit: v })} testid="f-price" />
            <Field label="Duration" v={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="f-duration" />
            <Field label="MOQ" v={form.moq} onChange={(v) => setForm({ ...form, moq: v })} testid="f-moq" />

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">Cities <span className="text-brand">(multiple allowed)</span></Label>
              <ChipInput
                values={form.cities}
                onChange={(vs) => setForm({ ...form, cities: vs })}
                suggestions={citySuggestions}
                placeholder="e.g. Bengaluru, Mumbai, Delhi NCR"
                testid="f-cities"
              />
            </div>

            {editing ? (
              <ServiceImagesSection
                serviceId={editing.id}
                serviceName={editing.service_name}
                images={form.images || []}
                onImagesChange={(imgs) => {
                  setForm((f) => ({ ...f, images: imgs }));
                  setEditing((e) => (e ? { ...e, images: imgs } : e));
                }}
              />
            ) : (
              <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-500">
                <div className="flex items-center gap-2 font-semibold text-zinc-700 mb-1">
                  <ImageSquare size={16} weight="duotone" className="text-brand" /> Sample Images
                </div>
                Save this service first, then re-open it in edit mode to upload up to <b>4 sample images</b> that will appear in generated quote PDFs.
              </div>
            )}

            {customColumns.length > 0 && (
              <div className="pt-4 mt-4 border-t border-zinc-200">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Custom fields</div>
                <div className="space-y-4">
                  {customColumns.map((c) => (
                    <Field
                      key={c.key}
                      label={c.label}
                      v={form.extra_fields[c.key] ?? ""}
                      onChange={(v) => setForm({ ...form, extra_fields: { ...form.extra_fields, [c.key]: v } })}
                      testid={`f-extra-${c.key}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-700">
              Cancel
            </Button>
            <Button onClick={save} data-testid="save-service-btn" className="bg-brand hover:bg-brand-hover text-white">
              {editing ? "Save changes" : "Add service"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Bulk upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-zinc-900">Bulk CSV Upload</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Required columns: <code className="text-brand font-mono-num">service_name, category, price_per_unit</code>.
              Optional: <code className="font-mono-num">duration, moq, cities</code> + any custom column keys.
              <br />
              <span className="text-zinc-600">Cities can be multiple, separated by <code className="font-mono-num">;</code> or <code className="font-mono-num">|</code> (e.g. <code className="font-mono-num">Bengaluru;Mumbai</code>).</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={downloadTemplate}
                variant="outline"
                size="sm"
                data-testid="dialog-download-template-btn"
                className="bg-white border-brand text-brand hover:bg-brand-soft gap-2"
              >
                <DownloadSimple size={14} weight="bold" /> Download CSV Template
              </Button>
              <span className="text-xs text-zinc-500">← Start here if you&apos;re unsure of the format</span>
            </div>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="csv-file-input"
              className="bg-white border-zinc-300 text-zinc-900 file:text-brand file:bg-transparent file:border-0 file:font-bold"
            />
            {uploadResult && (
              <div className="text-sm space-y-2 border border-zinc-200 bg-zinc-50 rounded-md p-3" data-testid="upload-result">
                <div className="text-emerald-700 flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" /> Inserted {uploadResult.inserted} rows
                </div>
                {uploadResult.errors?.length > 0 && (
                  <div className="text-red-700 text-xs">
                    <div className="mb-1 flex items-center gap-1"><XCircle size={14} weight="fill" /> {uploadResult.errors.length} errors</div>
                    <ul className="list-disc list-inside space-y-0.5 max-h-40 overflow-y-auto">
                      {uploadResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-700">Close</Button>
            <Button onClick={handleUpload} disabled={!file} data-testid="upload-submit-btn" className="bg-brand hover:bg-brand-hover text-white">Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</Label>
      <Input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-white border-zinc-300 text-zinc-900 focus-visible:ring-brand focus-visible:ring-offset-0"
      />
    </div>
  );
}

/* ================= Service Images ================= */
const MAX_IMAGES = 4;

function ServiceImagesSection({ serviceId, serviceName, images, onImagesChange }) {
  const [uploading, setUploading] = useState(false);
  const [nameMatchCount, setNameMatchCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const inputRef = React.useRef(null);

  // Load count of other services sharing this service_name
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/services/${serviceId}/name-matches`);
        setNameMatchCount(data?.matches || 0);
      } catch { /* ignore */ }
    })();
  }, [serviceId, serviceName]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (images.length >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images per service`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image exceeds 5 MB limit");
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Only PNG, JPG or WebP images are allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/services/${serviceId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onImagesChange(data.images);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (path) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const { data } = await api.delete(`/services/${serviceId}/images`, { data: { path } });
      onImagesChange(data.images);
      toast.success("Image removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const applyToAll = async () => {
    if (images.length === 0) {
      toast.error("Upload at least one image first");
      return;
    }
    const ok = window.confirm(
      `Apply these ${images.length} image(s) to all ${nameMatchCount} other "${serviceName}" services?\n\nThis will OVERWRITE any existing images on those services.`
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/services/${serviceId}/apply-images-to-name-matches`);
      toast.success(`Synced to ${data.updated} services`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="pt-4 mt-4 border-t border-zinc-200">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center gap-2">
          <ImageSquare size={14} weight="duotone" className="text-brand" />
          Sample Images
          <span className="text-zinc-400 normal-case tracking-normal font-medium">({images.length}/{MAX_IMAGES})</span>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
        These images are embedded in every generated quote PDF so clients can see real samples.
        PNG / JPG / WebP · max 5 MB each.
      </p>

      <div className="grid grid-cols-2 gap-3">
        {images.map((path) => (
          <ImageThumb key={path} path={path} onRemove={() => remove(path)} />
        ))}
        {images.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-service-image"
            className="aspect-video rounded-md border-2 border-dashed border-zinc-300 hover:border-brand hover:bg-brand-soft/50 transition-colors flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-brand disabled:opacity-50"
          >
            <ImageSquare size={22} weight="duotone" />
            <span className="text-xs font-semibold">{uploading ? "Uploading…" : "Add image"}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="service-image-input"
      />

      {/* Sync-to-matching-name section */}
      {nameMatchCount > 0 && (
        <div className="mt-4 rounded-md border border-dashed border-brand/40 bg-brand-soft/40 p-3" data-testid="sync-images-section">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center flex-shrink-0">
              <CopySimple size={16} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-900">Reuse these images for all locations</div>
              <p className="text-[11px] text-zinc-600 mt-0.5 leading-relaxed">
                <b>{nameMatchCount}</b> other services share the name <b>&ldquo;{serviceName}&rdquo;</b>.
                Copy these images to all of them in one click — no duplicate uploads.
              </p>
              <Button
                size="sm"
                onClick={applyToAll}
                disabled={syncing || images.length === 0}
                data-testid="apply-to-name-matches-btn"
                className="mt-2 bg-brand hover:bg-brand-hover text-white gap-1.5 h-8"
              >
                <CopySimple size={14} weight="bold" />
                {syncing ? "Syncing…" : `Apply to ${nameMatchCount} matching services`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageThumb({ path, onRemove }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let revoked = false;
    let objUrl = null;
    (async () => {
      const token = localStorage.getItem("bw_token");
      try {
        const res = await fetch(`${API}/files/${path}?auth=${encodeURIComponent(token)}`);
        if (!res.ok) return;
        const blob = await res.blob();
        objUrl = URL.createObjectURL(blob);
        if (!revoked) setSrc(objUrl);
      } catch { /* ignore */ }
    })();
    return () => {
      revoked = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [path]);

  return (
    <div className="relative group aspect-video rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden" data-testid={`img-thumb-${path}`}>
      {src ? (
        <img src={src} alt="Service sample" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs text-zinc-400">Loading…</div>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove image"
        className="absolute top-1.5 right-1.5 bg-white/95 hover:bg-red-600 hover:text-white text-red-600 rounded-full h-6 w-6 flex items-center justify-center shadow border border-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`img-remove-${path}`}
      >
        <CloseIcon size={12} weight="bold" />
      </button>
    </div>
  );
}

/* ================= Columns Panel ================= */
function ColumnsPanel({ columns, onChange }) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");

  const add = async () => {
    if (!label.trim() || !key.trim()) { toast.error("Label & key required"); return; }
    try {
      await api.post("/columns", { key, label });
      toast.success("Column added");
      setLabel(""); setKey("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (col) => {
    if (col.system) return;
    if (!window.confirm(`Delete column "${col.label}"? Existing values in this column will be lost.`)) return;
    try {
      await api.delete(`/columns/${col.key}`);
      toast.success("Column removed");
      onChange();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <h3 className="font-display font-bold text-zinc-900 text-xl mb-1">Add a new column</h3>
        <p className="text-zinc-500 text-sm mb-6">
          Extend the pricelist table. New columns appear on the pricelist and CSV/PDF exports.
        </p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">Header Label</Label>
            <Input
              value={label}
              onChange={(e) => {
                const l = e.target.value;
                setLabel(l);
                const derived = l
                  .toLowerCase()
                  .replace(/%/g, "_percent")
                  .replace(/&/g, "_and_")
                  .replace(/[^a-z0-9]+/g, "_")
                  .replace(/^_|_$/g, "");
                setKey(derived);
              }}
              placeholder="e.g. Tax %"
              data-testid="col-label"
              className="bg-white border-zinc-300 text-zinc-900"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">Column Key</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g. tax_percent"
              data-testid="col-key"
              className="bg-white border-zinc-300 text-zinc-900 font-mono-num"
            />
            <p className="text-[11px] text-zinc-400">Used internally &amp; in CSV headers.</p>
          </div>
          <Button onClick={add} data-testid="add-column-btn" className="bg-brand hover:bg-brand-hover text-white gap-2">
            <Plus size={16} weight="bold" /> Add Column
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-zinc-200 bg-cream text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Current columns</div>
        <table className="pricelist">
          <thead>
            <tr>
              <th>Order</th>
              <th>Label</th>
              <th>Key</th>
              <th>Type</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c, idx) => (
              <tr key={c.key} data-testid={`column-row-${c.key}`}>
                <td className="font-mono-num text-zinc-500">{String(idx + 1).padStart(2, "0")}</td>
                <td className="text-zinc-900 font-semibold">{c.label}</td>
                <td className="font-mono-num text-zinc-600">{c.key}</td>
                <td>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold ${c.system ? "bg-zinc-100 text-zinc-600 border border-zinc-200" : "bg-brand-soft text-brand border border-brand/20"}`}>
                    {c.system ? "System" : "Custom"}
                  </span>
                </td>
                <td className="text-right">
                  {!c.system && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(c)}
                      data-testid={`delete-col-${c.key}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                    >
                      <Trash size={14} /> Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Users Panel ================= */
function UsersPanel({ users, onChange }) {
  const [form, setForm] = useState({ email: "", name: "", password: "" });

  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("All fields required"); return; }
    try {
      await api.post("/users", form);
      toast.success("Sales user created");
      setForm({ email: "", name: "", password: "" });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); onChange(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <h3 className="font-display font-bold text-zinc-900 text-xl mb-1">Create sales user</h3>
        <p className="text-zinc-500 text-sm mb-6">Sales team can view the pricelist and export. Admin console remains locked.</p>
        <div className="space-y-4">
          <Field label="Full Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="u-name" />
          <Field label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="u-email" />
          <Field label="Temporary Password" type="password" v={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="u-password" />
          <Button onClick={create} data-testid="create-user-btn" className="bg-brand hover:bg-brand-hover text-white gap-2">
            <Plus size={16} weight="bold" /> Create User
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-zinc-200 bg-cream text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Sales team ({users.length})</div>
        <table className="pricelist">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Created</th><th className="text-right">Action</th></tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-10">No sales users yet.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="text-zinc-900 font-semibold">{u.name}</td>
                <td className="font-mono-num text-zinc-700">{u.email}</td>
                <td className="text-zinc-500 text-xs">{new Date(u.created_at).toLocaleString()}</td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1">
                    <Trash size={14} /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Logs Panel ================= */
function LogsPanel({ logs }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
      <div className="px-5 py-3 border-b border-zinc-200 bg-cream text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center justify-between">
        <span>Login audit trail</span>
        <span className="font-mono-num text-zinc-500">{logs.length} entries</span>
      </div>
      <div className="overflow-auto max-h-[calc(100vh-22rem)]">
        <table className="pricelist" data-testid="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>IP</th>
              <th>User Agent</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-10">No login attempts yet.</td></tr>}
            {logs.map((l) => (
              <tr key={l.id} data-testid={`log-row-${l.id}`}>
                <td className="font-mono-num text-zinc-700 text-xs">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="text-zinc-900 font-medium">{l.email}</td>
                <td className="text-zinc-600 uppercase text-xs tracking-widest font-bold">{l.role}</td>
                <td>
                  {l.status === "success" ? (
                    <span className="text-emerald-700 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
                      <CheckCircle size={14} weight="fill" /> Success
                    </span>
                  ) : (
                    <span className="text-red-700 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
                      <XCircle size={14} weight="fill" /> Failed
                    </span>
                  )}
                </td>
                <td className="font-mono-num text-zinc-600 text-xs">{l.ip}</td>
                <td className="text-zinc-500 text-xs truncate max-w-[280px]">{l.user_agent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
